"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ThingStore = void 0;
const ui_1 = require("@jsonjoy.com/ui");
const json_crdt_1 = require("json-joy/lib/json-crdt");
/**
 * Flat JSON CRDT `obj` map of every thing in the document, keyed by `@id`.
 * Each thing is its own child `obj` node, so per-field updates merge under
 * concurrent edits instead of replacing the whole record.
 */
class ThingStore {
    constructor(mutxt) {
        this.mutxt = mutxt;
        this.version = ui_1.rsync.val(0);
        this.idCnt = 0;
        this.nextId = () => {
            const cnt = this.idCnt++;
            const clock = this.mutxt.obj.api.model.clock;
            return `${clock.sid}-${clock.time}-${cnt}`;
        };
    }
    /**
     * Returns the `/things` obj API, creating it on the document if missing.
     * Only call from write paths — touching this on a read-only / pinned model
     * would mutate the model on mount and trigger pin enforcement loops.
     */
    get obj() {
        if (this._obj)
            return this._obj;
        const docObj = this.mutxt.obj;
        if (!docObj.has('things'))
            docObj.add('/things', json_crdt_1.s.obj({}));
        this._obj = docObj.obj('/things');
        return this._obj;
    }
    /** Returns the obj API only if `/things` already exists; never writes. */
    read() {
        if (this._obj)
            return this._obj;
        const docObj = this.mutxt.obj;
        if (!docObj.has('things'))
            return undefined;
        this._obj = docObj.obj('/things');
        return this._obj;
    }
    start() {
        const subscribe = (target) => target.onSubtreeChange(() => {
            this.version.next(this.version.value + 1);
        });
        const initial = this.read();
        if (initial) {
            this.unsub = subscribe(initial);
        }
        else {
            // `/things` doesn't exist yet. Watch the document for when it appears
            // (e.g. when the user pastes an image), then re-subscribe directly.
            const docObj = this.mutxt.obj;
            const docUnsub = docObj.onSubtreeChange(() => {
                const obj = this.read();
                if (!obj)
                    return;
                docUnsub();
                this.unsub = subscribe(obj);
                this.version.next(this.version.value + 1);
            });
            this.unsub = docUnsub;
        }
        return () => {
            this.unsub?.();
            this.unsub = undefined;
        };
    }
    has(id) {
        return this.read()?.has(id) ?? false;
    }
    get(id) {
        const obj = this.read();
        if (!obj || !obj.has(id))
            return undefined;
        const child = obj.obj(id, true);
        return child ? child.view() : undefined;
    }
    list(type) {
        const obj = this.read();
        if (!obj)
            return [];
        const view = obj.view();
        const result = [];
        for (const id in view) {
            const thing = view[id];
            if (!thing)
                continue;
            if (type && thing['@type'] !== type)
                continue;
            result.push(thing);
        }
        return result;
    }
    add(thing) {
        const id = thing['@id'] ?? this.nextId();
        this.obj.set({ [id]: { ...thing, '@id': id } });
        return id;
    }
    update(id, partial) {
        const obj = this.read();
        if (!obj || !obj.has(id))
            return false;
        const child = obj.obj(id, true);
        if (!child)
            return false;
        child.mergeKeys(partial);
        return true;
    }
    remove(id) {
        const obj = this.read();
        if (!obj || !obj.has(id))
            return false;
        obj.del([id]);
        return true;
    }
}
exports.ThingStore = ThingStore;
